/**
 * DocumentUploader — drag-and-drop + click-to-browse upload UI.
 *
 * Wraps the useUploadVaultDocument composite (SHA-256 → upload-init
 * → direct S3 PUT → finalize). Shows per-file progress + errors;
 * surfaces the 4xx codes the route emits (STORAGE_QUOTA_EXCEEDED,
 * CHECKSUM_MISMATCH, VIEWER_CANNOT_WRITE) as user-readable messages.
 *
 * Sensitivity defaults to 'standard' to match the route default;
 * uploaders can change it via the dropdown before submitting.
 *
 * V-1 single-part cap: 100MB per file (enforced server-side; the UI
 * just shows a clean error if the file is larger).
 */

import { useCallback, useRef, useState } from 'react';
import type { VaultDocumentSensitivityDto } from '@/lib/api';
import { useUploadVaultDocument } from '../hooks/useVaultDocuments';

/** V-1 single-part cap, also enforced server-side. */
export const MAX_UPLOAD_BYTES = 100 * 1024 * 1024;

interface DocumentUploaderProps {
  holderId: string;
  /** Defaults to 'standard'. Pass 'confidential' to bias the picker
   * for org Holders (members default to confidential there). */
  defaultSensitivity?: VaultDocumentSensitivityDto;
  /** When true, hides the sensitivity picker. The three tiers gate
   * role-based access (standard / confidential / proprietary) so they
   * only carry meaning on Org Holders — pass `true` for Personal Holder
   * uploads where the uploader is the sole accessor. */
  hideSensitivityPicker?: boolean;
  /** Target folder for new uploads. NULL/omitted = Holder root. */
  folderId?: string | null;
  /** Human-readable label for the destination folder; rendered under
   * the dropzone copy so the user can see where files will land. */
  folderLabel?: string;
  onUploaded?: () => void;
}

interface PendingUpload {
  file: File;
  status: 'pending' | 'uploading' | 'done' | 'error';
  error?: string;
}

export function DocumentUploader({
  holderId,
  defaultSensitivity = 'standard',
  hideSensitivityPicker = false,
  folderId,
  folderLabel,
  onUploaded,
}: DocumentUploaderProps) {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [sensitivity, setSensitivity] = useState<VaultDocumentSensitivityDto>(defaultSensitivity);
  const [pending, setPending] = useState<PendingUpload[]>([]);
  const [dragOver, setDragOver] = useState(false);
  const uploader = useUploadVaultDocument();

  const startUploads = useCallback(
    async (files: File[]) => {
      const next: PendingUpload[] = files.map((f) => ({ file: f, status: 'pending' }));
      setPending(next);
      for (let i = 0; i < next.length; i++) {
        const item = next[i];
        if (item.file.size > MAX_UPLOAD_BYTES) {
          item.status = 'error';
          item.error = 'File exceeds 100 MB upload limit.';
          setPending([...next]);
          continue;
        }
        item.status = 'uploading';
        setPending([...next]);
        try {
          await uploader.mutateAsync({
            holderId,
            file: item.file,
            sensitivity,
            folderId: folderId ?? null,
          });
          item.status = 'done';
          setPending([...next]);
        } catch (err) {
          const raw = err instanceof Error ? err.message : String(err);
          item.status = 'error';
          item.error = friendlyUploadError(raw);
          setPending([...next]);
        }
      }
      onUploaded?.();
    },
    [holderId, sensitivity, uploader, onUploaded, folderId],
  );

  const onFiles = (files: FileList | null) => {
    if (!files || files.length === 0) return;
    void startUploads(Array.from(files));
  };

  return (
    <div className="flex flex-col gap-3">
      <div
        onDragOver={(e) => {
          // Native OS file drags only. An in-app document or folder
          // drag would otherwise light this zone up as a valid target
          // and then drop into nothing.
          if (!e.dataTransfer.types.includes('Files')) return;
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          if (!e.dataTransfer.types.includes('Files')) return;
          e.preventDefault();
          setDragOver(false);
          onFiles(e.dataTransfer.files);
        }}
        className={`flex flex-col items-center justify-center rounded-lg border-2 border-dashed p-8 transition-colors ${
          dragOver
            ? 'border-primary bg-primary/5'
            : 'border-border bg-card hover:border-muted-foreground/50'
        }`}
      >
        <p className="text-sm text-muted-foreground">
          Drag and drop files here, or{' '}
          <button
            type="button"
            className="text-primary underline"
            onClick={() => fileInputRef.current?.click()}
          >
            browse
          </button>
        </p>
        <p className="mt-1 text-xs text-muted-foreground">PDF, DOCX, TXT, MD up to 100 MB each</p>
        {folderLabel && (
          <p className="mt-1 text-xs text-muted-foreground">
            Uploading to <span className="font-medium text-foreground">{folderLabel}</span>
          </p>
        )}
        <input
          ref={fileInputRef}
          type="file"
          multiple
          className="hidden"
          onChange={(e) => onFiles(e.target.files)}
        />
      </div>

      {!hideSensitivityPicker && (
        <label className="flex items-center gap-2 text-sm text-muted-foreground">
          Sensitivity:
          <select
            value={sensitivity}
            onChange={(e) => setSensitivity(e.target.value as VaultDocumentSensitivityDto)}
            className="rounded border border-border bg-card px-2 py-1 text-sm"
          >
            <option value="standard">Standard — visible to all roles</option>
            <option value="confidential">Confidential — member+ only</option>
            <option value="proprietary">Proprietary — admin/owner only</option>
          </select>
        </label>
      )}

      {pending.length > 0 && (
        <ul className="space-y-1 text-sm">
          {pending.map((p, i) => (
            <li
              key={i}
              className="flex items-center justify-between gap-3 rounded border border-border bg-card px-3 py-1.5"
            >
              <span className="truncate text-foreground">{p.file.name}</span>
              <span
                className={`text-xs ${
                  p.status === 'done'
                    ? 'text-green-700'
                    : p.status === 'error'
                      ? 'text-destructive'
                      : 'text-muted-foreground'
                }`}
              >
                {p.status === 'pending' && 'Queued'}
                {p.status === 'uploading' && 'Uploading…'}
                {p.status === 'done' && 'Done'}
                {p.status === 'error' && (p.error ?? 'Failed')}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** An upload failure in words a person can act on. */
export function friendlyUploadError(raw: string): string {
  if (raw.includes('STORAGE_QUOTA_EXCEEDED')) {
    return 'Storage quota exceeded for this workspace.';
  }
  if (raw.includes('VIEWER_CANNOT_WRITE')) {
    return 'You have viewer access only — uploads require member+ role.';
  }
  if (raw.includes('CHECKSUM_MISMATCH')) {
    return 'File integrity check failed. Try uploading again.';
  }
  if (raw.includes('TRUST_CHECK_FAILED')) {
    return 'You are not a member of this workspace.';
  }
  return raw.length > 200 ? raw.slice(0, 200) + '…' : raw;
}
